// Render-prop component to access multiple changed responses by array of ids. It does not
// update cache, it takes updated responses added by `ChangedResponse` component.
// Useful to fetch all existing changed responses to submit.
// Warning: it might have responses not for all fields for submission. You need to merge them
// with existing responses before updating.
// <ChangedResponse ids={[1, 2, 3]}>
//   {responses => (
//     <RenderResponsesOrSmth responses={responses} />
//   )}
// </ChangedResponse>
// The second parameter of children render-props is the update response function.
import * as React from 'react';

import type { UpdateChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponseContext';
import { ChangedResponseContext } from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponseContext';

import type { ResponseWithId } from './ResponseWithId';
import { asChangedResponse } from './ResponseWithId';
import type { ChangedResponse as ChangedResponseType } from './types';
import formChangedResponseId from './utils/formChangedResponseId';

type RenderProps = [Array<ChangedResponseType> | null | undefined, UpdateChangedResponse];

type Props = {
  ids: Array<string>;
  localScopeId?: string;

  // We optionally pass in responses with corresponding ids to augment the responses found in the
  // cache. This is for instances where some form parts aren't rendered under `ChangedResponse`.
  responses?: ResponseWithId[];

  children: (...args: RenderProps) => React.ReactNode;
};

const ChangedResponses = ({ ids, localScopeId, responses = [], children }: Props) => {
  const changedResponseIds = ids.map((id) => formChangedResponseId(id, localScopeId));
  const responseMap = responses.reduce((map, response) => ({ ...map, [response.id]: response }), {});

  const { getChangedResponse, updateChangedResponse } = React.useContext(ChangedResponseContext);

  const cachedResponses = changedResponseIds
    .map(getChangedResponse)
    .filter((changedResponse) => changedResponse.response);
  const cachedResponseMap = cachedResponses.reduce((map, response) => ({ ...map, [response.id]: response }), {});

  const returnedResponses = ids.reduce((acc, id) => {
    const changedResponseId = formChangedResponseId(id, localScopeId);

    // Find matching response from cache
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const cachedResponse = cachedResponseMap[changedResponseId] && {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      ...cachedResponseMap[changedResponseId],
      id,
    };

    // Find matching response from `responses` prop
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const responseFromProp = responseMap[id] && asChangedResponse(responseMap[id]);

    // Take cached response first, but otherwise take response from prop
    const matchingResponse = cachedResponse || responseFromProp;

    // Append matching response only if it exists
    return matchingResponse ? acc.concat(matchingResponse) : acc;
  }, [] as ChangedResponseType[]);

  // Children rely on mutation from <ChangedResponse>, which will produce returnedResponses.length > 0 when it is used
  // (i.e. ids.length > 0).
  if (ids.length > 0 && returnedResponses.length <= 0) {
    return <>{children(undefined, updateChangedResponse)}</>;
  }

  return <>{children(returnedResponses, updateChangedResponse)}</>;
};

export default ChangedResponses;
